import { Card, Grid } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { formattedTimeStamp } from 'pages/fulfillment/helpers'
import { useMemo, useState, Fragment } from 'react'
import { useOutletContext } from 'react-router-dom'
import {
    buildHistoricalProductivity,
    buildHistoricalTable,
    buildTablesForToday,
    buildShipOnTimeTable,
} from './ship-store-helpers'
import { buildDataTableObject } from 'utils/data'
import { useFulFillmentShipHistorical } from 'hooks/store-fulfillment/useStoreFulfillment'
import { BasicPlaceholder } from 'components/placeholder'
import { TimePeriods } from 'components/timeperiods'
import { ArrowComponent } from './ArrowComponent'
import useRefresh from 'hooks/utils/useRefresh'
import { useFlags } from '../../../../firebase/FirebaseRemoteConfig.provider'
import { ErrorAlert } from 'components/error-fallback'
import { ShipFromStoreData } from 'types/store-fulfillment'

type OutletContextType = {
    data?: Array<{ ship_from_store?: ShipFromStoreData | null }>
    refetch: () => void
    remove: () => void
}

export const StoreFulfillmentShipStore: React.FC = () => {
    const { data, refetch, remove } = useOutletContext<OutletContextType>()
    const { t, env, session, currentLocation } = useEssentials()
    const [viewIndex, setViewIndex] = useState<number>(0)
    const sfsData = data?.[0].ship_from_store

    useRefresh(refetch, remove)

    const { titles, timePeriods, headings } = TRANSLATION_KEYS.storeFulfillment
    const availableTimeDimensions = useMemo(
        () => [
            'today',
            'prior_day',
            'week_to_day',
            'prior_week',
            'month_to_day',
            'year_to_day',
        ],
        [],
    )
    const timePeriod = availableTimeDimensions[viewIndex]
    const timePeriodTranslationKeys = useMemo(
        () => [
            timePeriods.today,
            timePeriods.yesterday,
            timePeriods.weekToDate,
            timePeriods.lastWeek,
            timePeriods.monthToDate,
            timePeriods.yearToDate,
        ],
        [timePeriods],
    )

    const translatedTimePeriods = useMemo(
        () => [
            t(timePeriods.today),
            t(timePeriods.yesterday),
            t(timePeriods.weekToDate),
            t(timePeriods.lastWeek),
            t(timePeriods.monthToDate),
            t(timePeriods.yearToDate),
        ],
        [t, timePeriods],
    )

    const tableTitles = useMemo(
        () => [
            t(titles.carrierPickup),
            t(titles.totalUnitsToPack),
            t(titles.remainingAvailable),
            t(titles.teamMemberProductivity),
        ],
        [titles, t],
    )
    const historicalTableTitles = [
        t(headings.receivedUnits),
        t(headings.unitPlan),
        t(headings.unitsPacked),
    ]
    const historicalLabels = [
        t(headings.infPercent),
        t(headings.infDollars),
        t(headings.pickProductivity),
        t(headings.packProductivity),
    ]
    const {
        storesHistoricalData,
        shipOnTimeData,
        isLoading,
        isShipOnTimeLoading,
        isShipOnTimeError,
        shipOnTimeError,
        refetchShipOnTime,
    } = useFulFillmentShipHistorical({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters: {
            time_dimension: timePeriod,
        },
        enabled: timePeriod !== 'today',
    })
    const historicalTable = buildHistoricalTable(
        storesHistoricalData,
        historicalTableTitles,
    )
    const shipOnTimeHeadingKeys =
        TRANSLATION_KEYS.storeFulfillment.shipOnTimeHeadings
    const shipOnTimeHeadingTranslations = {
        location: t(shipOnTimeHeadingKeys.location),
        'original packages due': t(shipOnTimeHeadingKeys.originalPackagesDue),
        'accepted carrier issue packages': t(
            shipOnTimeHeadingKeys.acceptedCarrierIssuePackages,
        ),
        'total packages due': t(shipOnTimeHeadingKeys.totalPackagesDue),
        'ship on time %': t(shipOnTimeHeadingKeys.shipOnTimePercent),
        'ship on-time %': t(shipOnTimeHeadingKeys.shipOnTimePercent),
        'packages not sorted on time %': t(
            shipOnTimeHeadingKeys.packagesNotSortedOnTimePercent,
        ),
        'packages not shipped & no carrier issue submitted %': t(
            shipOnTimeHeadingKeys.packagesNotShippedNoCarrierIssuePercent,
        ),
        'mnr packages %': t(shipOnTimeHeadingKeys.mnrPackagesPercent),
        total: t(headings.total),
    }
    const shipOnTimeTable = buildShipOnTimeTable(
        shipOnTimeData,
        shipOnTimeHeadingTranslations,
    )
    const historicalProductivityTable = buildHistoricalProductivity(
        storesHistoricalData,
        historicalLabels,
    )

    const fulfillmentTitle = useMemo(() => {
        const timestamp = sfsData?.timestamp ?? 0
        return timePeriod === 'today'
            ? `${t(timePeriodTranslationKeys[viewIndex])} as of ${formattedTimeStamp(timestamp)}`
            : t(timePeriodTranslationKeys[viewIndex])
    }, [sfsData, t, timePeriod, timePeriodTranslationKeys, viewIndex])

    const handleTimeDimensionChange = (timePeriod: string, index: number) => {
        setViewIndex(index)
    }

    const { sortByTimeEnabled } = useFlags()
    const shipTableHeadings = useMemo(
        () =>
            sortByTimeEnabled
                ? { ...headings, shipByTime: headings.sortByTime }
                : headings,
        [headings, sortByTimeEnabled],
    )

    const tables = buildTablesForToday(sfsData, t, shipTableHeadings)
    return (
        <div>
            <TimePeriods
                timePeriods={translatedTimePeriods}
                activeTimePeriod={translatedTimePeriods[viewIndex]}
                onTabSelect={handleTimeDimensionChange}
                page="store fulfillment ship"
                isTimePeriodSticky={false}
            />
            <Grid.Container>
                <Grid.Item
                    className="hc8-pa-3x"
                    size={{
                        xs: 12,
                    }}
                >
                    <Grid.Item
                        className="hc8-pt-none"
                        size={{
                            xs: 12,
                        }}
                    >
                        {sfsData && (
                            <span aria-live="polite">{fulfillmentTitle}</span>
                        )}
                    </Grid.Item>

                    {timePeriod === 'today' && (
                        <>
                            <Grid.Item
                                className="hc8-pa-none"
                                size={{
                                    xs: 12,
                                }}
                            >
                                {sfsData && (
                                    <ArrowComponent sfsData={sfsData} />
                                )}
                            </Grid.Item>

                            {tables.map((table, index) => (
                                <Grid.Item
                                    key={index}
                                    size={{
                                        xs: 12,
                                    }}
                                >
                                    <p className="hc8-fs-050 hc8-mb-4x">
                                        {tableTitles[index]}
                                    </p>
                                    <Table
                                        scrollableWidth={index === 3}
                                        data={buildDataTableObject({
                                            currentData: table,
                                            widths: [100, 80, 80, 75, 75, 75],
                                            pinFirstColumn: true,
                                        })}
                                    />
                                </Grid.Item>
                            ))}
                        </>
                    )}
                    {timePeriod !== 'today' && (
                        <>
                            {isLoading ? (
                                <Grid.Item
                                    className="hc8-mt-4x"
                                    size={{
                                        xs: 12,
                                    }}
                                >
                                    <div
                                        className="placeholder-container"
                                        data-testid="placeholder"
                                    >
                                        <BasicPlaceholder
                                            type="table"
                                            rows={3}
                                            columns={3}
                                        />
                                    </div>
                                </Grid.Item>
                            ) : (
                                <>
                                    <Grid.Item
                                        className="hc8-mt-4x"
                                        size={{
                                            xs: 12,
                                        }}
                                    >
                                        <Table data={historicalTable} />
                                    </Grid.Item>
                                    <Grid.Item
                                        className="hc8-mt-3x"
                                        size={{
                                            xs: 12,
                                        }}
                                    >
                                        <p>
                                            {t(
                                                TRANSLATION_KEYS
                                                    .storeFulfillment
                                                    .shipOnTimeNote,
                                            )}
                                        </p>
                                    </Grid.Item>
                                    <Grid.Item
                                        className="hc8-mt-4x"
                                        size={{
                                            xs: 12,
                                        }}
                                    >
                                        {isShipOnTimeLoading ? (
                                            <div
                                                className="placeholder-container"
                                                data-testid="ship-on-time-placeholder"
                                            >
                                                <BasicPlaceholder
                                                    type="table"
                                                    rows={4}
                                                    columns={8}
                                                />
                                            </div>
                                        ) : isShipOnTimeError ? (
                                            <ErrorAlert
                                                error={shipOnTimeError}
                                                onRetry={refetchShipOnTime}
                                            />
                                        ) : shipOnTimeTable ? (
                                            <Table
                                                name={t(
                                                    TRANSLATION_KEYS
                                                        .storeFulfillment.ndd
                                                        .shippedOnTime,
                                                )}
                                                scrollableWidth
                                                data={shipOnTimeTable}
                                            />
                                        ) : (
                                            <ErrorAlert variant="no-data" />
                                        )}
                                    </Grid.Item>
                                    <Grid.Item
                                        size={{
                                            xs: 12,
                                        }}
                                    >
                                        <Card className="hc8-pa-4x hc8-mt-6x">
                                            <Grid.Container>
                                                {historicalProductivityTable.map(
                                                    (item, index) => (
                                                        <Fragment key={index}>
                                                            <Grid.Item
                                                                key={`${index}-label`}
                                                                className="hc8-pb-2x"
                                                                size={{
                                                                    xs: 6,
                                                                }}
                                                            >
                                                                {item.label}
                                                            </Grid.Item>
                                                            <Grid.Item
                                                                key={`${index}-value`}
                                                                className={`hc8-pb-2x hc8-ta-right ${item.className ?? ''}`}
                                                                size={{
                                                                    xs: 6,
                                                                }}
                                                            >
                                                                <span>
                                                                    {item.value}{' '}
                                                                    {item.icon}
                                                                </span>
                                                            </Grid.Item>
                                                        </Fragment>
                                                    ),
                                                )}
                                            </Grid.Container>
                                        </Card>
                                    </Grid.Item>
                                </>
                            )}
                        </>
                    )}
                </Grid.Item>
            </Grid.Container>
        </div>
    )
}
